export const supplierClaimsEn = {
  title: "Supplier claims",
  intro:
    "Claims against a supplier when the cause is their material or processing. Uses the same issue list as customer complaints. The approved amount is what the supplier owes.",
  create: "New supplier claim",
  search: "Search claim, supplier, product or lot",
  empty: "No supplier claims.",
  error: "Unable to load or save the supplier claim.",
  back: "Back to supplier claims",
  invalid: "Check the supplier, issue type, claims and description before saving.",
  claimNo: "Claim",
  supplier: "Supplier",
  chooseSupplier: "Choose a supplier",
  source: "Source",
  sourceKinds: { CustomerCase: "Customer complaint", Ncr: "NCR", Manual: "Entered manually" },
  product: "Product",
  lot: "Lot number",
  po: "Purchase order",
  none: "None",
  description: "What happened",
  descriptionHint: "The supplier reads this on the supplier portal. Leave out customer names and selling prices.",
  claimed: "Claimed",
  agreed: "Approved amount",
  status: "Status",
  allStatuses: "All statuses",
  allSuppliers: "All suppliers",
  statuses: {
    Draft: "Draft",
    Sent: "Sent to supplier",
    Responded: "Supplier responded",
    Approved: "Approved",
    Closed: "Closed",
    Cancelled: "Cancelled",
  },
  suggestions: "Suggested suppliers",
  suggestionsHint: "Pick one to fill in the supplier, product, lot and purchase order. Check the basis before sending.",
  noSuggestions: "No supplier could be traced from this source. Choose the supplier yourself.",
  basis: {
    lot: "Traced by lot",
    linkedPo: "Purchase order linked to the PI",
    product: "Default supplier of the product",
    ncr: "Recorded on the NCR",
  },
  fromCase: "From customer complaint {{no}}",
  caseText: "Customer complaint text (reference only)",
  caseTextHint: "Not sent to the supplier. Rewrite what the supplier needs to know under What happened above.",
  fromNcr: "From NCR {{no}}",
  response: "Supplier response",
  responses: { Accepted: "Accepted", Disputed: "Disputed" },
  responseNote: "Supplier explanation",
  respondedBy: "Recorded by",
  approvedBy: "Approved by",
  reason: "Reason",
  editReason: "Reason for the change",
  history: "History",
  events: {
    Created: "Created",
    Edited: "Edited",
    Sent: "Sent to supplier",
    Responded: "Supplier response recorded",
    Approved: "Amount approved",
    Closed: "Closed",
    Cancelled: "Cancelled",
    SupplierResponded: "Supplier responded in the portal",
    DebitNoteReplaced: "Debit note reversed and replaced",
  },
  actions: {
    send: "Send to supplier",
    recordResponse: "Record supplier response",
    approve: "Approve amount",
    close: "Close",
    cancel: "Cancel claim",
  },
  actionHint: "Enter a reason. Every step is kept in the history.",
  approveHint: "The approved amount cannot exceed the claimed amount. It is what the supplier owes.",
  approveNoMoney: "This claim has no amount. Approving records the outcome only.",
  linked: "Supplier claims from this complaint",
  createFromCase: "Raise a supplier claim",
  noLinked: "No supplier claim yet.",
  needsSupplierCause: "Set the root cause to “Supplier material or processing” and save to raise a supplier claim.",
  createFromNcr: "Raise supplier claim",
  errors: {
    selfApproval: "You raised this claim, so you cannot approve its amount yourself — someone else with the Approve tick does.",
    resend: "The claim was edited after the supplier replied. Send it to the supplier again before approving the amount.",
    locked: "This claim cannot be changed in its current status.",
    source: "The source, supplier or purchase order is not valid for this claim.",
    response: "Record whether the supplier accepted. A dispute needs an explanation.",
    approval: "Enter an approved amount no larger than the claimed amount.",
    changed: "Someone else changed this claim. Reload and try again.",
    ticketClaimed: "This complaint already has a supplier claim. Cancel that claim before changing the root cause.",
    debitNote: "Close this claim after finance posts its debit note.",
  },
  debitNote: "Debit note",
  debitNoteStatuses: { Draft: "Draft · waiting for finance to post", Posted: "Posted · deducted from payables", Reversed: "Reversed" },
  debitNoteHint: "Approving an amount creates this debit note. Finance posts it and deducts it from the supplier's invoices.",
  openInFinance: "Open in Finance",
  rulesTitle: "Rules in force",
  rulesDecided: "Settled 20/09/2026",
  groups: {
    supplier: "Supplier and faulty goods",
    supplierHint: "Who is liable, which product, lot and purchase order.",
    claim: "Claim",
    claimHint: "Defect type, severity, what is claimed and the amount; the description the supplier can read.",
    decision: "Response and approval",
    decisionHint: "What the supplier answered, the approved amount and the debit note.",
  },
  selfApproval: "You raised this claim, so you cannot approve its amount yourself — someone else with the Approve tick does.",
  resendNeeded: "Edited after the supplier replied — their answer was for the previous version. Send the claim again; it can be approved once the supplier answers.",
  awaitingApproval: "The supplier has replied. Waiting for someone with the Approve tick to approve the amount.",
  rules: {
    source: {
      title: "Where it comes from, whom to claim",
      body: "Raised from a customer complaint concluded as the supplier's material or processing, from an incoming-inspection NCR, or by hand. The system suggests the supplier with its basis — traced lot, purchase order linked to the PI, the product's default supplier; the author checks before sending. The same defect list as customer complaints.",
    },
    response: {
      title: "Supplier response",
      body: "A supplier with a portal account accepts or disputes on the Supplier portal; otherwise staff use Record response (the history shows who said it). A disputed claim is adjusted and sent again; a claim edited after the supplier replied must be sent again before its amount is approved. A sent claim is not edited silently; the supplier never sees the customer name, selling prices or internal notes.",
    },
    money: {
      title: "Approval is a money decision",
      body: "Someone with the Approve tick on this menu approves the amount — never more than claimed — and the author never approves their own claim, admins included. Approving an amount creates a draft debit note at once; accounting posts it under Finance (Approve tick there) and applies it to the supplier's invoices. A claim with money closes only after the debit note is posted.",
    },
    authority: {
      title: "Who does what",
      body: "Create to raise; Edit to correct, send, record the response, close and cancel; Approve to approve the amount (not the author). Posting, reversing and applying debit notes belong to the Approve tick on Finance.",
    },
  },
};

export const supplierClaimsVi: typeof supplierClaimsEn = {
  title: "Khiếu nại nhà cung cấp",
  intro:
    "Đòi nhà cung cấp khi nguyên nhân là vật liệu hoặc gia công của họ. Dùng cùng danh sách lỗi với khiếu nại của khách. Số tiền được duyệt là khoản nhà cung cấp phải chịu.",
  create: "Lập khiếu nại nhà cung cấp",
  search: "Tìm số hồ sơ, nhà cung cấp, mã hàng hoặc số lô",
  empty: "Chưa có khiếu nại nhà cung cấp.",
  error: "Không tải hoặc lưu được khiếu nại nhà cung cấp.",
  back: "Về danh sách khiếu nại nhà cung cấp",
  invalid: "Kiểm tra nhà cung cấp, loại lỗi, yêu cầu và mô tả trước khi lưu.",
  claimNo: "Số hồ sơ",
  supplier: "Nhà cung cấp",
  chooseSupplier: "Chọn nhà cung cấp",
  source: "Nguồn",
  sourceKinds: { CustomerCase: "Khiếu nại của khách", Ncr: "NCR", Manual: "Lập tay" },
  product: "Sản phẩm",
  lot: "Số lô",
  po: "Đơn mua hàng",
  none: "Không có",
  description: "Sự việc",
  descriptionHint: "Nhà cung cấp đọc được phần này trên Cổng nhà cung cấp. Đừng ghi tên khách hay giá bán.",
  claimed: "Số tiền đòi",
  agreed: "Số tiền duyệt",
  status: "Trạng thái",
  allStatuses: "Mọi trạng thái",
  allSuppliers: "Mọi nhà cung cấp",
  statuses: {
    Draft: "Nháp",
    Sent: "Đã gửi nhà cung cấp",
    Responded: "Nhà cung cấp đã phản hồi",
    Approved: "Đã duyệt",
    Closed: "Đã đóng",
    Cancelled: "Đã huỷ",
  },
  suggestions: "Nhà cung cấp gợi ý",
  suggestionsHint: "Chọn một dòng để điền nhà cung cấp, sản phẩm, số lô và đơn mua. Kiểm căn cứ trước khi gửi.",
  noSuggestions: "Không truy được nhà cung cấp từ nguồn này. Hãy tự chọn nhà cung cấp.",
  basis: {
    lot: "Truy theo lô hàng",
    linkedPo: "Đơn mua gắn với PI",
    product: "Nhà cung cấp mặc định của sản phẩm",
    ncr: "Ghi sẵn trên NCR",
  },
  fromCase: "Từ khiếu nại của khách {{no}}",
  caseText: "Chữ trong hồ sơ khiếu nại của khách (chỉ để tham khảo)",
  caseTextHint: "Phần này không gửi nhà cung cấp. Viết lại điều nhà cung cấp cần biết vào ô Sự việc ở trên.",
  fromNcr: "Từ NCR {{no}}",
  response: "Phản hồi của nhà cung cấp",
  responses: { Accepted: "Chấp nhận", Disputed: "Không đồng ý" },
  responseNote: "Giải thích của nhà cung cấp",
  respondedBy: "Người ghi nhận",
  approvedBy: "Người duyệt",
  reason: "Lý do",
  editReason: "Lý do sửa",
  history: "Lịch sử",
  events: {
    Created: "Đã lập",
    Edited: "Đã sửa",
    Sent: "Đã gửi nhà cung cấp",
    Responded: "Đã ghi nhận phản hồi",
    Approved: "Đã duyệt số tiền",
    Closed: "Đã đóng",
    Cancelled: "Đã huỷ",
    SupplierResponded: "Nhà cung cấp phản hồi trên cổng",
    DebitNoteReplaced: "Phiếu ghi nợ bị đảo và lập phiếu thay thế",
  },
  actions: {
    send: "Gửi nhà cung cấp",
    recordResponse: "Ghi nhận phản hồi",
    approve: "Duyệt số tiền",
    close: "Đóng",
    cancel: "Huỷ hồ sơ",
  },
  actionHint: "Nhập lý do. Mọi bước đều được lưu vào lịch sử.",
  approveHint: "Số tiền duyệt không được lớn hơn số tiền đòi. Đây là khoản nhà cung cấp phải chịu.",
  approveNoMoney: "Hồ sơ không có số tiền. Duyệt chỉ ghi nhận kết quả.",
  linked: "Khiếu nại nhà cung cấp từ hồ sơ này",
  createFromCase: "Lập khiếu nại nhà cung cấp",
  noLinked: "Chưa lập khiếu nại nhà cung cấp.",
  needsSupplierCause: "Chọn nguyên nhân “Vật liệu / gia công của nhà cung cấp” rồi lưu để lập khiếu nại nhà cung cấp.",
  createFromNcr: "Lập khiếu nại NCC",
  errors: {
    selfApproval: "Bạn lập hồ sơ này nên không tự duyệt số tiền — người khác có ô Duyệt sẽ duyệt.",
    resend: "Hồ sơ đã sửa sau khi nhà cung cấp phản hồi. Gửi lại cho nhà cung cấp rồi mới duyệt số tiền.",
    locked: "Không sửa được hồ sơ ở trạng thái hiện tại.",
    source: "Nguồn, nhà cung cấp hoặc đơn mua không hợp lệ cho hồ sơ này.",
    response: "Ghi rõ nhà cung cấp chấp nhận hay không. Không đồng ý thì phải có giải thích.",
    approval: "Nhập số tiền duyệt, không lớn hơn số tiền đòi.",
    changed: "Người khác vừa sửa hồ sơ. Tải lại rồi thử lại.",
    ticketClaimed: "Hồ sơ này đã có khiếu nại nhà cung cấp. Huỷ khiếu nại đó trước khi đổi nguyên nhân.",
    debitNote: "Đóng hồ sơ sau khi kế toán ghi sổ phiếu ghi nợ.",
  },
  debitNote: "Phiếu ghi nợ",
  debitNoteStatuses: { Draft: "Nháp · chờ kế toán ghi sổ", Posted: "Đã ghi sổ · đã trừ vào công nợ phải trả", Reversed: "Đã đảo" },
  debitNoteHint: "Duyệt số tiền là sinh phiếu này. Kế toán ghi sổ và trừ vào hoá đơn của nhà cung cấp.",
  openInFinance: "Mở ở Tài chính",
  rulesTitle: "Quy tắc áp dụng",
  rulesDecided: "Đã chốt 20/09/2026",
  groups: {
    supplier: "Nhà cung cấp và hàng lỗi",
    supplierHint: "Ai chịu, hàng nào, lô nào, đơn mua nào.",
    claim: "Khiếu nại",
    claimHint: "Loại lỗi, mức độ, đòi gì và số tiền; phần sự việc nhà cung cấp đọc được.",
    decision: "Phản hồi và duyệt",
    decisionHint: "Nhà cung cấp trả lời gì, số tiền duyệt, phiếu ghi nợ.",
  },
  selfApproval: "Bạn lập hồ sơ này nên không tự duyệt số tiền — người khác có ô Duyệt sẽ duyệt.",
  resendNeeded: "Đã sửa sau khi nhà cung cấp phản hồi — câu trả lời đó là cho nội dung cũ. Gửi lại cho nhà cung cấp; có phản hồi mới thì duyệt được.",
  awaitingApproval: "Nhà cung cấp đã phản hồi. Chờ người có ô Duyệt duyệt số tiền.",
  rules: {
    source: {
      title: "Lập từ đâu, đòi ai",
      body: "Lập từ khiếu nại của khách đã kết luận nguyên nhân vật liệu / gia công của nhà cung cấp, từ NCR kiểm hàng nhập, hoặc lập tay. Hệ gợi ý nhà cung cấp kèm căn cứ — truy theo lô, đơn mua gắn PI, nhà cung cấp mặc định của sản phẩm; người lập kiểm trước khi gửi. Dùng cùng danh mục lỗi với khiếu nại của khách.",
    },
    response: {
      title: "Nhà cung cấp phản hồi",
      body: "Nhà cung cấp có tài khoản cổng tự Chấp nhận / Không đồng ý trên Cổng nhà cung cấp; chưa có cổng thì nhân viên bấm Ghi nhận phản hồi (lịch sử ghi đúng ai nói). Không đồng ý thì điều chỉnh rồi gửi lại; hồ sơ sửa sau khi nhà cung cấp đã phản hồi phải gửi lại trước khi duyệt số tiền. Hồ sơ đã gửi không sửa lặng lẽ; nhà cung cấp không thấy tên khách, giá bán hay ghi chú nội bộ.",
    },
    money: {
      title: "Duyệt là quyết định tiền",
      body: "Người có ô Duyệt ở menu này duyệt số tiền — không lớn hơn số đòi; người lập không tự duyệt hồ sơ của mình, kể cả quản trị viên. Duyệt có tiền là sinh ngay phiếu ghi nợ nháp; kế toán ghi sổ ở Tài chính (ô Duyệt ở đó) và trừ vào hoá đơn của nhà cung cấp. Hồ sơ có tiền chỉ đóng được khi phiếu đã ghi sổ.",
    },
    authority: {
      title: "Ai được làm gì",
      body: "Tạo để lập; Sửa để sửa, gửi, ghi nhận phản hồi, đóng, huỷ; Duyệt để duyệt số tiền (không phải người lập). Ghi sổ, đảo, trừ phiếu ghi nợ là việc của ô Duyệt ở menu Tài chính.",
    },
  },
};

export const supplierClaimsZh: typeof supplierClaimsEn = {
  title: "供应商索偿",
  intro: "当原因是供应商的材料或加工时向供应商索偿。与客户投诉使用相同的问题清单。审核金额即供应商应承担的金额。",
  create: "新增供应商索偿",
  search: "搜索索偿单号、供应商、品号或批号",
  empty: "尚无供应商索偿。",
  error: "无法加载或保存供应商索偿。",
  back: "返回供应商索偿",
  invalid: "保存前请核对供应商、问题类型、索偿项目与说明。",
  claimNo: "索偿单号",
  supplier: "供应商",
  chooseSupplier: "选择供应商",
  source: "来源",
  sourceKinds: { CustomerCase: "客户投诉", Ncr: "NCR", Manual: "手动创建" },
  product: "产品",
  lot: "批号",
  po: "采购单",
  none: "无",
  description: "事件说明",
  descriptionHint: "供应商可在供应商门户看到此内容。请勿填写客户名称或售价。",
  claimed: "索偿金额",
  agreed: "审核金额",
  status: "状态",
  allStatuses: "全部状态",
  allSuppliers: "全部供应商",
  statuses: {
    Draft: "草稿",
    Sent: "已发送给供应商",
    Responded: "供应商已回复",
    Approved: "已审核",
    Closed: "已关闭",
    Cancelled: "已取消",
  },
  suggestions: "建议供应商",
  suggestionsHint: "选择一条以带入供应商、产品、批号与采购单。发送前请核对依据。",
  noSuggestions: "无法从此来源追溯供应商，请自行选择。",
  basis: {
    lot: "依批号追溯",
    linkedPo: "与形式发票关联的采购单",
    product: "产品的默认供应商",
    ncr: "NCR 已记录",
  },
  fromCase: "来自客户投诉 {{no}}",
  caseText: "客户投诉记录原文（仅供参考）",
  caseTextHint: "此内容不会发送给供应商。请在上方“事件说明”中改写供应商需要知道的内容。",
  fromNcr: "来自 NCR {{no}}",
  response: "供应商回复",
  responses: { Accepted: "接受", Disputed: "不同意" },
  responseNote: "供应商说明",
  respondedBy: "记录人",
  approvedBy: "审核人",
  reason: "原因",
  editReason: "修改原因",
  history: "历史记录",
  events: {
    Created: "已创建",
    Edited: "已修改",
    Sent: "已发送给供应商",
    Responded: "已记录回复",
    Approved: "已审核金额",
    Closed: "已关闭",
    Cancelled: "已取消",
    SupplierResponded: "供应商于门户回复",
    DebitNoteReplaced: "扣款单已冲销并创建替代单",
  },
  actions: {
    send: "发送给供应商",
    recordResponse: "记录供应商回复",
    approve: "审核金额",
    close: "关闭",
    cancel: "取消索偿",
  },
  actionHint: "请填写原因。每个步骤都会保留在历史记录中。",
  approveHint: "审核金额不得大于索偿金额，即供应商应承担的金额。",
  approveNoMoney: "此索偿无金额，审核仅记录结果。",
  linked: "此投诉产生的供应商索偿",
  createFromCase: "创建供应商索偿",
  noLinked: "尚未创建供应商索偿。",
  needsSupplierCause: "将根本原因设为“供应商材料或加工”并保存后，才能创建供应商索偿。",
  createFromNcr: "创建供应商索偿",
  errors: {
    selfApproval: "此索偿由您创建，您不能自行审核金额——由其他有“审核”勾选的人审核。",
    resend: "此索偿在供应商回复后被修改。请先重新发送给供应商，再审核金额。",
    locked: "目前状态无法修改此索偿。",
    source: "来源、供应商或采购单对此索偿无效。",
    response: "请记录供应商是否接受；不同意时须附说明。",
    approval: "请输入不大于索偿金额的审核金额。",
    changed: "其他人刚修改了此索偿，请重新加载后再试。",
    ticketClaimed: "此投诉已有供应商索偿。变更根本原因前请先取消该索偿。",
    debitNote: "财务过账扣款单后才能结案。",
  },
  debitNote: "扣款单",
  debitNoteStatuses: { Draft: "草稿 · 待财务过账", Posted: "已过账 · 已冲减应付", Reversed: "已冲销" },
  debitNoteHint: "审核金额即创建此扣款单。由财务过账并抵扣供应商发票。",
  openInFinance: "在财务中打开",
  rulesTitle: "适用规则",
  rulesDecided: "已于20/09/2026确定",
  groups: {
    supplier: "供应商与不良货品",
    supplierHint: "由谁承担、哪个产品、批次与采购单。",
    claim: "索偿",
    claimHint: "问题类型、严重度、索偿内容与金额；供应商可阅读的事实描述。",
    decision: "回复与审核",
    decisionHint: "供应商的回复、审核金额及扣款单。",
  },
  selfApproval: "此索偿由您创建，您不能自行审核金额——由其他有“审核”勾选的人审核。",
  resendNeeded: "供应商回复后已修改——该回复针对的是旧内容。请重新发送给供应商；收到新回复后才能审核。",
  awaitingApproval: "供应商已回复，等待有“审核”勾选的人审核金额。",
  rules: {
    source: {
      title: "来源与索偿对象",
      body: "由已判定为供应商材料／加工原因的客户投诉、来料检验 NCR 创建，或人工创建。系统会附依据建议供应商——按批次追溯、PI 关联的采购单、产品默认供应商；创建人于发送前核对。与客户投诉使用相同的问题清单。",
    },
    response: {
      title: "供应商回复",
      body: "有门户账号的供应商在供应商门户自行“接受／不同意”；未开通门户时由员工按“记录回复”（历史会记下由谁转述）。不同意时调整后重新发送；供应商回复后才修改的索偿，须重新发送才能审核金额。已发送的索偿不会被悄悄修改；供应商看不到客户名称、售价或内部备注。",
    },
    money: {
      title: "审核属于金额决策",
      body: "由在此菜单有“审核”勾选的人审核金额——不得超过索偿金额；创建人不能审核自己的索偿，管理员亦然。审核有金额即同时生成扣款单草稿；由会计在“财务”（该处的“审核”勾选）过账并抵扣供应商发票。有金额的索偿须待扣款单过账后才能结案。",
    },
    authority: {
      title: "谁可以做什么",
      body: "“创建”可创建；“编辑”可修改、发送、记录回复、结案、取消；“审核”可审核金额（非创建人）。扣款单的过账、冲销、抵扣属于“财务”菜单的“审核”。",
    },
  },
};
